// src/services/demoApi/fileDownload.ts
// Скачивание файлов хранилища в настоящих форматах (FR-2).
// Генераторы подгружаются динамически — в основной бандл не попадают (NFR-2).
// Загруженные файлы (upload-*) отдаются исходным Blob байт-в-байт (FR-5.2).
// Оформление — фирменная палитра приложения (navy/blue, см. core/theme).
import type { DamFile } from 'mocks/dam';
import type { DamFileContent, DamTextContent } from 'mocks/damContents';
import { getDamFileContent } from './damApi';
import { parseBlocks, runsToPlainText, type InlineRun, type MdBlock } from './markdownBlocks';
// CRA отдаёт URL на шрифт; DejaVu Sans содержит кириллицу для jsPDF
// eslint-disable-next-line import/no-extraneous-dependencies
import dejaVuUrl from 'dejavu-fonts-ttf/ttf/DejaVuSans.ttf';

// Палитра документов = палитра приложения (core/theme/index.ts), hex без «#»
const NAVY = '1D3557'; // заголовки, шапки таблиц
const BLUE = '1474FF'; // акценты, линии
const INK = '1F2933'; // основной текст
const GREY = '5C6670'; // вторичный текст, футеры
const BORDER = 'D9DEE8'; // границы таблиц
const ZEBRA = 'F3F6FB'; // чётные строки таблиц

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Текстовое представление для xlsx-генератора, если контент текстовый */
function asTable(content: DamFileContent): { headers: string[]; rows: string[][] } {
  if (content.kind === 'table') return { headers: content.headers, rows: content.rows };
  return {
    headers: ['Раздел', 'Содержание'],
    rows: content.sections.map((s) => [s.heading, s.body]),
  };
}

function asText(content: DamFileContent): DamTextContent {
  if (content.kind === 'text') return content;
  return {
    kind: 'text',
    sections: [
      {
        heading: 'Таблица',
        body: [content.headers.join(' | '), ...content.rows.map((r) => r.join(' | '))].join('\n'),
      },
    ],
  };
}

/** Блоки секции: rich из bodyMd, иначе строка = абзац (старые/статические данные) */
function sectionBlocks(section: { body: string; bodyMd?: string }): MdBlock[] {
  if (section.bodyMd) return parseBlocks(section.bodyMd);
  return section.body
    .split('\n')
    .filter((line) => line.trim())
    .map((line) => ({ type: 'paragraph', runs: [{ text: line }] }));
}

// ───────────────────────────── DOCX ─────────────────────────────

export async function generateDocx(
  content: DamFileContent,
  title: string,
  includeTitle = true
): Promise<Blob> {
  const docx = await import('docx');
  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    LevelFormat,
    ShadingType,
    Footer,
    PageNumber,
  } = docx;

  const text = asText(content);
  const toRuns = (runs: InlineRun[]) =>
    runs.map(
      (run) =>
        new TextRun({
          text: run.text,
          bold: run.bold,
          italics: run.italic,
          font: run.code ? 'Consolas' : undefined,
        })
    );

  const thinBorder = { style: BorderStyle.SINGLE, size: 4, color: BORDER };
  const cellBorders = {
    top: thinBorder,
    bottom: thinBorder,
    left: thinBorder,
    right: thinBorder,
  };
  const cellMargins = { top: 80, bottom: 80, left: 120, right: 120 };

  const blockToElements = (
    block: MdBlock
  ): Array<InstanceType<typeof Paragraph | typeof Table>> => {
    switch (block.type) {
      case 'paragraph':
        return [new Paragraph({ children: toRuns(block.runs), spacing: { after: 120 } })];
      case 'bullets':
        return block.items.map(
          (item) =>
            new Paragraph({
              children: toRuns(item),
              bullet: { level: 0 },
              spacing: { after: 60 },
            })
        );
      case 'numbered':
        return block.items.map(
          (item) =>
            new Paragraph({
              children: toRuns(item),
              numbering: { reference: 'doc-numbering', level: 0 },
              spacing: { after: 60 },
            })
        );
      case 'quote':
        return [
          new Paragraph({
            children: block.runs.map(
              (run) => new TextRun({ text: run.text, italics: true, color: GREY })
            ),
            indent: { left: 480 },
            border: { left: { style: BorderStyle.SINGLE, size: 18, color: BLUE, space: 12 } },
            spacing: { after: 120 },
          }),
        ];
      case 'table':
        return [
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: [
              new TableRow({
                tableHeader: true,
                children: block.headers.map(
                  (header) =>
                    new TableCell({
                      borders: cellBorders,
                      margins: cellMargins,
                      shading: { type: ShadingType.SOLID, color: NAVY, fill: NAVY },
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({ text: header, bold: true, color: 'FFFFFF', size: 20 }),
                          ],
                        }),
                      ],
                    })
                ),
              }),
              ...block.rows.map(
                (row, rowIdx) =>
                  new TableRow({
                    children: row.map(
                      (cell) =>
                        new TableCell({
                          borders: cellBorders,
                          margins: cellMargins,
                          shading:
                            rowIdx % 2 === 1
                              ? { type: ShadingType.SOLID, color: ZEBRA, fill: ZEBRA }
                              : undefined,
                          children: [
                            new Paragraph({
                              children: [new TextRun({ text: cell, size: 20 })],
                            }),
                          ],
                        })
                    ),
                  })
              ),
            ],
          }),
          new Paragraph({ children: [], spacing: { after: 120 } }),
        ];
      default:
        return [];
    }
  };

  const titleParagraph = includeTitle
    ? [new Paragraph({ text: title, heading: HeadingLevel.TITLE })]
    : [];
  const children = [
    ...titleParagraph,
    ...text.sections.flatMap((section) => [
      new Paragraph({ text: section.heading, heading: HeadingLevel.HEADING_1 }),
      ...sectionBlocks(section).flatMap(blockToElements),
    ]),
  ];

  const doc = new Document({
    styles: {
      default: { document: { run: { font: 'Calibri', size: 22, color: INK } } },
      paragraphStyles: [
        {
          id: 'Title',
          name: 'Title',
          basedOn: 'Normal',
          next: 'Normal',
          run: { size: 44, bold: true, color: NAVY },
          paragraph: {
            spacing: { after: 240 },
            border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: BLUE, space: 8 } },
          },
        },
        {
          id: 'Heading1',
          name: 'Heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { size: 28, bold: true, color: NAVY },
          paragraph: { spacing: { before: 280, after: 140 } },
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { size: 24, bold: true, color: NAVY },
          paragraph: { spacing: { before: 200, after: 100 } },
        },
      ],
    },
    numbering: {
      config: [
        {
          reference: 'doc-numbering',
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.START,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
          ],
        },
      ],
    },
    sections: [
      {
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({ text: `${title}  ·  стр. `, size: 16, color: GREY }),
                  new TextRun({ children: [PageNumber.CURRENT], size: 16, color: GREY }),
                ],
              }),
            ],
          }),
        },
        children,
      },
    ],
  });
  return Packer.toBlob(doc);
}

// ───────────────────────────── XLSX ─────────────────────────────

const NUMERIC_RE = /^-?\d{1,3}(?:[ \u00A0\u202F]?\d{3})*(?:[.,]\d+)?%?$/;

export async function generateXlsx(content: DamFileContent, title: string): Promise<Blob> {
  const ExcelJS = (await import('exceljs')).default ?? (await import('exceljs'));
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(title.slice(0, 31) || 'Лист 1', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });
  const { headers, rows } = asTable(content);

  const thin = { style: 'thin' as const, color: { argb: `FF${BORDER}` } };
  const borders = { top: thin, bottom: thin, left: thin, right: thin };

  const headerRow = sheet.addRow(headers);
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${NAVY}` } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = borders;
  });

  rows.forEach((row, rowIdx) => {
    const added = sheet.addRow(
      row.map((value) => {
        const trimmed = value.trim();
        if (NUMERIC_RE.test(trimmed) && !trimmed.endsWith('%')) {
          return parseFloat(trimmed.replace(/[ \u00A0\u202F]/g, '').replace(',', '.'));
        }
        return value;
      })
    );
    added.eachCell((cell) => {
      cell.border = borders;
      cell.alignment = {
        vertical: 'top',
        horizontal: typeof cell.value === 'number' ? 'right' : 'left',
        wrapText: true,
      };
      if (rowIdx % 2 === 1) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${ZEBRA}` } };
      }
    });
  });

  // Ширина колонок по содержимому: min 12, max 50 символов
  headers.forEach((header, colIdx) => {
    const longest = Math.max(header.length, ...rows.map((row) => (row[colIdx] ?? '').length));
    sheet.getColumn(colIdx + 1).width = Math.min(50, Math.max(12, longest + 4));
  });
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

// ───────────────────────────── PDF ─────────────────────────────

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

async function generatePdf(
  content: DamFileContent,
  title: string,
  includeTitle = true
): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const fontResponse = await fetch(dejaVuUrl);
  const fontBase64 = arrayBufferToBase64(await fontResponse.arrayBuffer());
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  doc.addFileToVFS('DejaVuSans.ttf', fontBase64);
  doc.addFont('DejaVuSans.ttf', 'DejaVu', 'normal');
  doc.setFont('DejaVu');

  const text = asText(content);
  const pageWidth = doc.internal.pageSize.getWidth() - 80;
  let y = 60;
  const ensureRoom = (needed: number) => {
    if (y + needed > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage();
      y = 60;
    }
  };
  const writeLines = (raw: string, size: number, gapAfter: number, color = INK, indent = 0) => {
    doc.setFontSize(size);
    doc.setTextColor(...hexToRgb(color));
    const lines = doc.splitTextToSize(raw, pageWidth - indent) as string[];
    lines.forEach((line) => {
      ensureRoom(size * 1.4);
      doc.text(line, 40 + indent, y);
      y += size * 1.4;
    });
    y += gapAfter;
  };

  if (includeTitle) {
    writeLines(title, 18, 2, NAVY);
    ensureRoom(8);
    doc.setDrawColor(...hexToRgb(BLUE));
    doc.setLineWidth(2);
    doc.line(40, y - 6, 40 + pageWidth, y - 6);
    y += 14;
  }

  text.sections.forEach((section) => {
    writeLines(section.heading, 13, 2, NAVY);
    ensureRoom(6);
    doc.setDrawColor(...hexToRgb(BLUE));
    doc.setLineWidth(1);
    doc.line(40, y - 6, 160, y - 6);
    y += 8;
    sectionBlocks(section).forEach((block) => {
      if (block.type === 'paragraph') writeLines(runsToPlainText(block.runs), 10, 6);
      else if (block.type === 'quote') writeLines(runsToPlainText(block.runs), 10, 6, GREY, 16);
      else if (block.type === 'bullets' || block.type === 'numbered') {
        block.items.forEach((item, idx) => {
          const marker = block.type === 'bullets' ? '•' : `${idx + 1}.`;
          writeLines(`${marker}  ${runsToPlainText(item)}`, 10, 2, INK, 12);
        });
        y += 4;
      } else if (block.type === 'table') {
        writeLines(block.headers.join('  ·  '), 10, 2, NAVY);
        block.rows.forEach((row) => writeLines(row.join('  ·  '), 10, 2, INK, 8));
        y += 6;
      }
    });
    y += 8;
  });
  return doc.output('blob');
}

// ───────────────────────────── PPTX ─────────────────────────────

const MAX_LINES_PER_SLIDE = 7;

interface PptxLine {
  runs: InlineRun[];
  bullet: false | 'bullet' | number;
}

/** Блоки секции → плоские строки слайда (для разбивки длинных секций) */
function blocksToPptxLines(blocks: MdBlock[]): { lines: PptxLine[]; table: MdBlock | null } {
  const lines: PptxLine[] = [];
  let table: MdBlock | null = null;
  blocks.forEach((block) => {
    if (block.type === 'paragraph') lines.push({ runs: block.runs, bullet: false });
    else if (block.type === 'quote')
      lines.push({ runs: block.runs.map((r) => ({ ...r, italic: true })), bullet: false });
    else if (block.type === 'bullets')
      block.items.forEach((item) => lines.push({ runs: item, bullet: 'bullet' }));
    else if (block.type === 'numbered')
      block.items.forEach((item, idx) => lines.push({ runs: item, bullet: idx + 1 }));
    else if (block.type === 'table' && !table) table = block;
  });
  return { lines, table };
}

export async function generatePptx(content: DamFileContent, title: string): Promise<Blob> {
  const PptxGenJS = (await import('pptxgenjs')).default;
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_16x9';
  pptx.theme = { headFontFace: 'Calibri', bodyFontFace: 'Calibri' };

  pptx.defineSlideMaster({
    title: 'GLB_TITLE',
    background: { color: NAVY },
    objects: [
      { rect: { x: 0, y: 4.9, w: '100%', h: 0.14, fill: { color: BLUE } } },
      { rect: { x: 0.6, y: 1.7, w: 1.4, h: 0.06, fill: { color: BLUE } } },
    ],
  });
  pptx.defineSlideMaster({
    title: 'GLB_CONTENT',
    background: { color: 'FFFFFF' },
    objects: [
      { rect: { x: 0, y: 0, w: '100%', h: 0.16, fill: { color: NAVY } } },
      {
        text: {
          text: title,
          options: { x: 0.6, y: 5.2, w: 6, h: 0.3, fontSize: 9, color: GREY, align: 'left' },
        },
      },
    ],
    slideNumber: { x: 9.2, y: 5.2, w: 0.5, h: 0.3, color: GREY, fontSize: 9 },
  });

  const text = asText(content);

  // Титульный слайд нужен всегда: для презентации имя документа — естественный
  // заголовок, а не дублирующая шапка (в отличие от docx/pdf, FR-6.2)
  const titleSlide = pptx.addSlide({ masterName: 'GLB_TITLE' });
  titleSlide.addText(title, {
    x: 0.6,
    y: 2.0,
    w: 8.8,
    h: 1.4,
    fontSize: 32,
    bold: true,
    color: 'FFFFFF',
    valign: 'top',
  });
  titleSlide.addText('Сформировано ИИ-агентом Globus Context', {
    x: 0.6,
    y: 3.4,
    w: 8.8,
    h: 0.4,
    fontSize: 13,
    color: 'B9C6DA',
  });

  const addContentSlide = (heading: string, lines: PptxLine[], table: MdBlock | null) => {
    const slide = pptx.addSlide({ masterName: 'GLB_CONTENT' });
    slide.addText(heading, {
      x: 0.6,
      y: 0.45,
      w: 8.8,
      h: 0.6,
      fontSize: 22,
      bold: true,
      color: NAVY,
    });
    slide.addShape('rect', { x: 0.6, y: 1.08, w: 1.1, h: 0.045, fill: { color: BLUE } });

    let contentY = 1.35;
    if (lines.length > 0) {
      const textObjs = lines.flatMap((line) =>
        line.runs.map((run, runIdx) => ({
          text: run.text,
          options: {
            bold: run.bold,
            italic: run.italic,
            breakLine: runIdx === line.runs.length - 1,
            ...(runIdx === 0 && line.bullet
              ? {
                  bullet:
                    line.bullet === 'bullet'
                      ? { code: '2022', indent: 14 }
                      : { type: 'number' as const, indent: 14 },
                }
              : {}),
          },
        }))
      );
      const textHeight = Math.min(3.6, 0.42 * lines.length + 0.2);
      slide.addText(textObjs, {
        x: 0.6,
        y: contentY,
        w: 8.8,
        h: textHeight,
        fontSize: 14,
        color: INK,
        valign: 'top',
        paraSpaceAfter: 8,
      });
      contentY += textHeight + 0.15;
    }
    if (table && table.type === 'table') {
      const tableRows = [
        table.headers.map((header) => ({
          text: header,
          options: { bold: true, color: 'FFFFFF', fill: { color: NAVY } },
        })),
        ...table.rows.map((row, rowIdx) =>
          row.map((cell) => ({
            text: cell,
            options: { color: INK, fill: { color: rowIdx % 2 === 1 ? ZEBRA : 'FFFFFF' } },
          }))
        ),
      ];
      slide.addTable(tableRows, {
        x: 0.6,
        y: contentY,
        w: 8.8,
        fontSize: 11,
        border: { type: 'solid', pt: 0.5, color: BORDER },
        valign: 'middle',
        autoPage: false,
      });
    }
  };

  text.sections.forEach((section) => {
    const { lines, table } = blocksToPptxLines(sectionBlocks(section));
    if (lines.length === 0 && !table) return;
    // Авторазбиение длинных секций: не более MAX_LINES_PER_SLIDE строк на слайд
    const chunks: PptxLine[][] = [];
    for (let i = 0; i < lines.length; i += MAX_LINES_PER_SLIDE) {
      chunks.push(lines.slice(i, i + MAX_LINES_PER_SLIDE));
    }
    if (chunks.length === 0) chunks.push([]);
    chunks.forEach((chunk, chunkIdx) => {
      const heading = chunkIdx === 0 ? section.heading : `${section.heading} (продолжение)`;
      const isLast = chunkIdx === chunks.length - 1;
      addContentSlide(heading, chunk, isLast ? table : null);
    });
  });

  const blob = (await pptx.write({ outputType: 'blob' })) as Blob;
  return blob;
}

// ───────────────────────────── Экспорт ─────────────────────────────

/** Скачать файл хранилища. Бросает Error с понятным сообщением (EC-6, EC-7) */
export async function downloadDamFile(file: DamFile): Promise<void> {
  // Загруженный файл — исходный Blob байт-в-байт (FR-5.2)
  if (file.id.startsWith('upload-')) {
    const { getUploadedBlob } = await import('./damUploads');
    const blob = await getUploadedBlob(file.id);
    if (!blob) throw new Error('Файл не найден в локальном хранилище');
    saveBlob(blob, `${file.name}.${file.ext}`);
    return;
  }

  const content = await getDamFileContent(file.id);
  if (!content) throw new Error('У файла нет содержимого'); // EC-6

  const filename = `${file.name}.${file.ext}`;
  const includeTitle = !file.id.startsWith('created-'); // FR-6.2: без имени-заголовка для сгенерированных
  if (file.ext === 'docx') saveBlob(await generateDocx(content, file.name, includeTitle), filename);
  else if (file.ext === 'xlsx') saveBlob(await generateXlsx(content, file.name), filename);
  else if (file.ext === 'pdf')
    saveBlob(await generatePdf(content, file.name, includeTitle), filename);
  else if (file.ext === 'pptx') saveBlob(await generatePptx(content, file.name), filename);
  else {
    const { contentToPlainText } = await import('mocks/damContents');
    saveBlob(
      new Blob([contentToPlainText(content)], { type: 'text/plain;charset=utf-8' }),
      filename
    );
  }
}
